import type { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { SafeImage } from '@/components/safe-image.tsx';
import { Header } from '@/components/header.tsx';
import { LoadingSpinner } from '@/components/loading-spinner.tsx';
import { TabBar } from '@/components/tab-bar.tsx';
import { fetchEvents } from '@/lib/event.api.ts';
import { MSG } from '@/lib/constants.ts';
import { askAlert } from '@/store/dialog.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import type { EventSummary } from '@/types/event.ts';
import type { PageProps } from '@/types/props.ts';

/** 상태 배지 문구 */
const STATUS_LABEL: Record<EventSummary['status'], string> = {
  ongoing: '진행중',
  ended: '종료',
};

const LIST_TITLE = '진행중인 이벤트';

/**
 * 이벤트 목록 (시안 `_event.html`).
 *
 * 내용은 `MC_EVENT` 에서 온다. 상태(진행중/종료)는 서버가 **노출 설정과 기간을
 * 함께 보고** 정한다 — 노출을 켜 두어도 종료일이 지나면 종료다.
 */
const Event: FunctionalComponent<PageProps> = () => {
  const dispatch = useAppDispatch();
  const [items, setItems] = useState<EventSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const response = await fetchEvents(1);
        if (cancelled) return;
        if (!response.result) {
          await dispatch(askAlert(response.message ?? MSG.apiFail));
          return;
        }
        setItems(response.data?.items ?? []);
      } catch {
        if (!cancelled) await dispatch(askAlert(MSG.apiFail));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  return (
    <div class="page">
      <Header />

      <div class="section">
        <div class="contents">
          <div class="event-section">
            <div class="section-head">
              <span class="section-title">{LIST_TITLE}</span>
            </div>

            {loading && <LoadingSpinner show />}

            <div class="event-list">
              {items.map((item) => {
                const ended = item.status === 'ended';

                return (
                  <a
                    key={item.id}
                    href={ended ? '#' : `/event/${item.id}`}
                    class="event-card"
                    onClick={(e) => {
                      e.preventDefault();
                      // 시안도 종료된 카드에는 링크를 걸지 않았다. 다만 아무 반응이
                      // 없으면 고장으로 보이므로 안내를 띄운다.
                      if (ended) void dispatch(askAlert('종료된 이벤트입니다.'));
                      else route(`/event/${item.id}`);
                    }}
                  >
                    <div class="event-card-thumb">
                      <SafeImage src={item.thumbnail} alt={item.title} />
                      <span class={ended ? 'event-card-status ended' : 'event-card-status'}>
                        {STATUS_LABEL[item.status]}
                      </span>
                    </div>
                    <div class="event-card-info">
                      <div class="event-card-title">{item.title}</div>
                      <div class="event-card-period">{item.period}</div>
                    </div>
                  </a>
                );
              })}
            </div>

            {!loading && items.length === 0 && (
              <p class="hint" style={{ textAlign: 'center', padding: '2rem 0' }}>
                진행중인 이벤트가 없습니다.
              </p>
            )}
          </div>
        </div>
      </div>

      <TabBar path="/event" />
    </div>
  );
};

export default Event;
